import useScrollLock from '@/hooks/useScrollLock';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useDarkMode } from '@/hooks/useDarkMode';

import { logger } from '@/utils/logger';
import { Bell, Calendar, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
export default function OccasionsModal({ isOpen, onClose, recipient, onUpdate }) {
  const darkMode = useDarkMode();
  useScrollLock(isOpen);
  const [occasions, setOccasions] = useState([]);
  const [reminderTiming, setReminderTiming] = useState(null);
  const [newOccasion, setNewOccasion] = useState({ name: '', date: '' });
  const [saving, setSaving] = useState(false);

  // Update state when recipient changes
  useEffect(() => {
    if (recipient) {
      setOccasions(recipient.occasions || []);
      setReminderTiming(recipient.reminder_timing || null);
    }
  }, [recipient]);

  const reminderOptions = [
    { value: 60, label: '60 days' },
    { value: 30, label: '30 days' },
    { value: 14, label: '2 weeks' },
    { value: 7, label: '1 week' },
    { value: 3, label: '3 days' },
    { value: 1, label: '1 day' }
  ];

  const handleAddOccasion = () => {
    if (!newOccasion.name || !newOccasion.date) {
      toast.error('Please enter both occasion name and date');
      return;
    }
    
    setOccasions([...occasions, { ...newOccasion }]);
    setNewOccasion({ name: '', date: '' });
  };

  const handleRemoveOccasion = (index) => {
    setOccasions(occasions.filter((_, i) => i !== index));
  };

  const toggleReminderDay = (day) => {
    if (!reminderTiming) {
      // If null, start with the selected day
      setReminderTiming([day]);
    } else {
      if (reminderTiming.includes(day)) {
        const updated = reminderTiming.filter(d => d !== day);
        setReminderTiming(updated.length === 0 ? null : updated);
      } else {
        setReminderTiming([...reminderTiming, day]);
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedRecipient = {
        ...recipient,
        occasions,
        reminder_timing: reminderTiming
      };
      await onUpdate(updatedRecipient);
      toast.success('Occasions and reminders updated!');
      onClose();
    } catch (error) {
      logger.error('Error saving occasions:', error);
      toast.error('Failed to save occasions');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !recipient) return null;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        background: darkMode.isDark ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 99999,
        pointerEvents: 'auto'
      }}
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden transition-colors max-h-[90vh] overflow-y-auto"
        style={{ 
          background: darkMode.bgSecondary,
          pointerEvents: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b transition-colors" style={{ borderColor: darkMode.isDark ? '#FED7A5' : '#E5E7EB' }}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 rounded-full transition-colors"
            style={{ 
              background: darkMode.isDark ? 'rgba(254, 215, 165, 0.2)' : 'transparent',
              color: darkMode.isDark ? '#FED7A5' : '#73766A'
            }}
          >
            <X className="w-5 h-5" />
          </button>
          <h3 className="text-xl font-bold transition-colors" style={{ color: darkMode.textPrimary, fontFamily: 'Playfair Display, serif' }}>
            Occasions for {recipient.name}
          </h3>
          <p className="text-sm mt-1 transition-colors" style={{ color: darkMode.textSecondary }}>
            Manage important dates and reminder preferences
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Occasions */}
          <div>
            <h4 className="text-sm font-semibold mb-3 transition-colors" style={{ color: darkMode.textPrimary }}>
              Current Occasions
            </h4>
            {occasions.length === 0 ? (
              <p className="text-sm transition-colors" style={{ color: darkMode.textSecondary }}>
                No occasions added yet
              </p>
            ) : (
              <div className="space-y-2">
                {occasions.map((occasion, index) => (
                  <div 
                    key={occasion.id || `${occasion.name}-${occasion.date}-${index}`}
                    className="flex items-center justify-between p-3 rounded-lg transition-colors"
                    style={{ background: darkMode.isDark ? 'rgba(255,255,255,0.05)' : '#F9FAFB' }}
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4" style={{ color: '#9E6752' }} />
                      <div>
                        <p className="font-medium transition-colors" style={{ color: darkMode.textPrimary }}>
                          {occasion.name}
                        </p>
                        <p className="text-sm transition-colors" style={{ color: darkMode.textSecondary }}>
                          {new Date(occasion.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveOccasion(index)}
                      className="p-2 rounded-lg transition-colors hover:bg-red-100 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add New Occasion */}
          <div>
            <h4 className="text-sm font-semibold mb-3 transition-colors" style={{ color: darkMode.textPrimary }}>
              Add New Occasion
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm mb-1 block transition-colors" style={{ color: darkMode.textSecondary }}>
                  Occasion Name
                </label>
                <Input
                  value={newOccasion.name}
                  onChange={(e) => setNewOccasion({ ...newOccasion, name: e.target.value })}
                  placeholder="e.g., Birthday, Anniversary"
                  className="transition-colors"
                  style={{ 
                    background: darkMode.isDark ? 'rgba(255,255,255,0.05)' : 'white',
                    borderColor: darkMode.borderPrimary,
                    color: darkMode.textPrimary
                  }}
                />
              </div>
              <div>
                <label className="text-sm mb-1 block transition-colors" style={{ color: darkMode.textSecondary }}>
                  Date
                </label>
                <Input
                  type="date"
                  value={newOccasion.date}
                  onChange={(e) => setNewOccasion({ ...newOccasion, date: e.target.value })}
                  className="transition-colors"
                  style={{ 
                    background: darkMode.isDark ? 'rgba(255,255,255,0.05)' : 'white',
                    borderColor: darkMode.borderPrimary,
                    color: darkMode.textPrimary
                  }}
                />
              </div>
              <Button
                onClick={handleAddOccasion}
                variant="outline"
                className="w-full"
                style={{ borderColor: '#9E6752', color: '#9E6752' }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Occasion
              </Button>
            </div>
          </div>

          {/* Reminder Preferences */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4" style={{ color: '#9E6752' }} />
              <h4 className="text-sm font-semibold transition-colors" style={{ color: darkMode.textPrimary }}>
                Reminder Preferences for {recipient.name}
              </h4>
            </div>
            <p className="text-xs mb-3 transition-colors" style={{ color: darkMode.textSecondary }}>
              {reminderTiming === null 
                ? "Using global reminder settings. Click days below to set custom reminders for this person."
                : "Custom reminders set for this person. Click to toggle off and use global settings."}
            </p>
            <div className="flex flex-wrap gap-2">
              {reminderOptions.map((option) => {
                const isActive = reminderTiming?.includes(option.value);
                return (
                  <button
                    key={option.value}
                    onClick={() => toggleReminderDay(option.value)}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                    style={{
                      background: isActive ? '#9E6752' : (darkMode.isDark ? 'rgba(255,255,255,0.1)' : 'white'),
                      color: isActive ? 'white' : darkMode.textPrimary,
                      border: `1px solid ${isActive ? '#9E6752' : darkMode.borderPrimary}`
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {reminderTiming !== null && (
              <button
                onClick={() => setReminderTiming(null)}
                className="text-xs mt-2 underline transition-colors"
                style={{ color: darkMode.textSecondary }}
              >
                Reset to use global settings
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t transition-colors flex gap-3" style={{ borderColor: darkMode.isDark ? '#FED7A5' : '#E5E7EB' }}>
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            style={{ borderColor: darkMode.borderPrimary, color: darkMode.textPrimary }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1"
            style={{ background: '#9E6752', color: 'white' }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
